import React from 'react';
import { Box, Container, ContainerProps, useTheme, SxProps, Theme } from '@mui/material';

type SectionVariant = 'default' | 'white' | 'gradient' | 'dark';

interface SectionContainerProps {
  children: React.ReactNode;
  variant?: SectionVariant;
  containerMaxWidth?: ContainerProps['maxWidth'];
  disableContainer?: boolean;
  compact?: boolean;
  id?: string;
  sx?: SxProps<Theme>;
}

/**
 * Section wrapper component with various background styles
 * Provides consistent padding and optional Container wrapper
 *
 * @param variant - Background style: 'default' (light gray), 'white', 'gradient', or 'dark'
 * @param containerMaxWidth - Container max width (default: 'lg')
 * @param disableContainer - If true, children are rendered without Container wrapper
 * @param compact - If true, uses reduced vertical padding
 */
export const SectionContainer: React.FC<SectionContainerProps> = ({
  children,
  variant = 'default',
  containerMaxWidth = 'lg',
  disableContainer = false,
  compact = false,
  id,
  sx,
}) => {
  const theme = useTheme();

  const getBackgroundStyles = () => {
    switch (variant) {
      case 'white':
        return { bgcolor: 'background.paper' };
      case 'gradient':
        return {
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: 'white',
        };
      case 'dark':
        return { bgcolor: '#2D2D2D', color: 'white' };
      case 'default':
      default:
        return { bgcolor: 'background.default' };
    }
  };

  const content = disableContainer ? (
    children
  ) : (
    <Box sx={{ px: { xs: 1, sm: 0 } }}>
      <Container maxWidth={containerMaxWidth}>{children}</Container>
    </Box>
  );

  return (
    <Box
      component="section"
      id={id}
      sx={[
        {
          py: compact ? { xs: 4, md: 6 } : { xs: 6, md: 10 },
          ...getBackgroundStyles(),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {content}
    </Box>
  );
};
